import { components } from '@feedfm/clips-services-api';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useStudioAuthenticatedContext } from '../../../context/StudioContext';
import { useToast } from '../../../context/ToastContext';
import { useDebounce } from '../../../hooks/useDebounce';
import { useUrlState } from '../../../hooks/useUrlState';
import { CollectionSortBy } from '../../../util/collectionSort';
import type { CatalogSortColumn } from './catalogSortColumns';
import { VALID_SORT_COLUMNS } from './catalogSortColumns';
import { hasActiveFilters } from './hasActiveFilters';
import {
  DEFAULT_SEARCH_SCOPE,
  isSearchScope,
  searchFilterPatch,
  type SearchScope,
} from '../../../components/CatalogFilterModel/catalogSearchScope';
import { stripScopedSearchKeys } from './stripScopedSearchKeys';

type TrackObject = components['schemas']['TrackObject'];
type ServerCatalogFilters =
  components['schemas']['CurationCatalogRequest']['filters'];

/**
 * Client-only filter key. Custom clips live in the in-memory store rather than
 * the catalog API, so this selection is carried alongside the server filters
 * for URL persistence and chips, then stripped before the request is sent and
 * applied to the visible rows in Catalog.
 */
export const CUSTOM_CLIPS_FILTER_KEY = 'customClips';
export type CustomClipsFilterValue = 'only' | 'hide';

/**
 * Prototype placeholder filter keys for the mocked Language / Popularity
 * columns. They have no catalog-API backing yet, so — like the custom-clips key
 * — they persist in the URL and drive chips but are stripped before the request.
 */
export const LANGUAGE_FILTER_KEY = 'languageContains';
export const POPULARITY_FILTER_KEY = 'popularityMin';

/**
 * Filter keys the catalog API doesn't understand. Stripped from every request;
 * carried in the filters object only for URL persistence and active-filter
 * chips. `customClips` is additionally applied to the visible rows in Catalog;
 * the language/popularity placeholders are inert.
 */
export const CLIENT_ONLY_FILTER_KEYS = [
  CUSTOM_CLIPS_FILTER_KEY,
  LANGUAGE_FILTER_KEY,
  POPULARITY_FILTER_KEY,
];

/**
 * The generated API filters type plus the client-only keys above. This
 * intersection — and the three keys it adds — is a stand-in until backend
 * integration makes them real server-side filters; remove it once the API
 * actually models `customClips`/`languageContains`/`popularityMin`.
 *
 * This is the single source of truth: other modules should import this type
 * rather than re-deriving it from `components['schemas']['CurationCatalogRequest']['filters']`.
 */
export type CatalogFiltersType = ServerCatalogFilters & {
  customClips?: CustomClipsFilterValue;
  languageContains?: string;
  popularityMin?: number;
};

export interface UseCatalogTableReturn {
  tracks: TrackObject[];
  loading: boolean;
  error: string | null;
  page: number;
  pageSize: number;
  total: number;
  searchQuery: string;
  searchScope: SearchScope;
  filters: CatalogFiltersType | undefined;
  sortBy: CatalogSortColumn;
  sortOrder: 'asc' | 'desc';
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  setSearchQuery: (query: string) => void;
  setSearchScope: (scope: SearchScope) => void;
  setFilters: (
    filters: CatalogFiltersType | undefined,
    resetPage?: boolean,
  ) => void;
  setSortBy: (column: CatalogSortColumn) => void;
  setSortOrder: (order: 'asc' | 'desc') => void;
  handleSort: (column: CatalogSortColumn) => void;
  handlePlayTrack: (track: TrackObject) => void;
  isTrackPlaying: (trackId: number) => boolean;
}

export const useCatalogTable = (): UseCatalogTableReturn => {
  const { backendClient, playerState } = useStudioAuthenticatedContext();
  const { showToast } = useToast();
  const { getParam, getNumberParam, setParams } = useUrlState();

  // ─── State ──────────────────────────────────────────────────────────────────

  const [tracks, setTracks] = useState<TrackObject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(() => (getNumberParam('page', 1) ?? 1) - 1);
  const [pageSize, setPageSize] = useState(
    () => getNumberParam('pageSize', 20) ?? 20,
  );
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState(
    () => getParam('search') || '',
  );
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const [searchScope, setSearchScopeState] = useState<SearchScope>(() => {
    const urlScope = getParam('searchScope');
    return isSearchScope(urlScope) ? urlScope : DEFAULT_SEARCH_SCOPE;
  });

  const [filters, setFiltersState] = useState<CatalogFiltersType | undefined>(
    () => {
      const urlFilters = getParam('filters');
      if (urlFilters) {
        try {
          // Strip keys that scoped search now owns — stale bookmarks from
          // before the scoped-search feature could still carry these.
          return stripScopedSearchKeys(JSON.parse(urlFilters)) as CatalogFiltersType;
        } catch {
          return {};
        }
      }
      return {};
    },
  );

  const [sortBy, setSortBy] = useState<CatalogSortColumn>(() => {
    const urlSortBy = getParam('sortBy') as CatalogSortColumn | null;
    return urlSortBy && VALID_SORT_COLUMNS.includes(urlSortBy)
      ? urlSortBy
      : null;
  });
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(() => {
    const urlSortOrder = getParam('sortOrder');
    return urlSortOrder === 'desc' ? 'desc' : 'asc';
  });

  // ─── URL Sync ───────────────────────────────────────────────────────────────

  useEffect(() => {
    const hasFiltersActive = hasActiveFilters(filters);
    setParams({
      search: searchQuery || null,
      searchScope: searchScope !== DEFAULT_SEARCH_SCOPE ? searchScope : null,
      page: page > 0 ? page + 1 : null,
      pageSize: pageSize !== 20 ? pageSize : null,
      sortBy: sortBy || null,
      sortOrder: sortBy && sortOrder !== 'asc' ? sortOrder : null,
      filters: hasFiltersActive ? JSON.stringify(filters) : null,
    });
  }, [
    searchQuery,
    searchScope,
    page,
    pageSize,
    sortBy,
    sortOrder,
    filters,
    setParams,
  ]);

  // ─── Data Fetching ──────────────────────────────────────────────────────────

  // The catalog request only carries server-understood filters — client-only
  // keys (custom clips, language/popularity placeholders) are dropped. Memoize
  // on the stripped *content* (via its JSON) so toggling a client-only filter,
  // which changes `filters` identity but not the request, doesn't refetch.
  const serverFiltersJson = useMemo(() => {
    const stripped = { ...(filters ?? {}) } as Record<string, unknown>;
    for (const key of CLIENT_ONLY_FILTER_KEYS) delete stripped[key];
    return JSON.stringify(stripped);
  }, [filters]);
  const serverFilters = useMemo(
    () => JSON.parse(serverFiltersJson) as CatalogFiltersType,
    [serverFiltersJson],
  );

  const fetchTracks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const request: components['schemas']['CurationCatalogRequest'] = {
        pagination: {
          limit: pageSize,
          offset: page * pageSize,
        },
        filters: {
          ...serverFilters,
          ...searchFilterPatch(searchScope, debouncedSearchQuery),
        },
        sortBy:
          (sortBy as components['schemas']['CurationCatalogRequest']['sortBy']) ||
          undefined,
        sortOrder,
      };

      const response = await backendClient.getCurationCatalog(request);
      setTracks(response.tracks || []);
      setTotal(response.pagination?.total || 0);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'An error occurred while fetching tracks',
      );
      showToast('Failed to fetch tracks', 'error');
    } finally {
      setLoading(false);
    }
  }, [
    backendClient,
    page,
    pageSize,
    debouncedSearchQuery,
    searchScope,
    serverFilters,
    sortBy,
    sortOrder,
    showToast,
  ]);

  useEffect(() => {
    fetchTracks();
  }, [fetchTracks]);

  // ─── Playback ───────────────────────────────────────────────────────────────

  const handlePlayTrack = useCallback(
    (track: TrackObject) => {
      if (!track.track_preview?.url) {
        showToast('No preview available for this track', 'warning');
        return;
      }

      const singleTrackCollection: components['schemas']['Collection'] = {
        collectionId: track.id,
        collectionName: track.title,
        description: `Full track: ${track.title}`,
        publishedOn: null,
        rating: 'unknown',
        explicit: false,
        clips: [
          {
            clipId: track.id,
            trackId: track.id,
            title: track.title,
            artist: track.display_artist_name,
            album: track.release_title,
            duration: track.duration_in_seconds,
            preSignedUrl: track.track_preview.url,
            preSignedUrlExpires: '',
            encoding: '',
            bitrate: 0,
            explicitness: track.explicitness
              ? (track.explicitness.toLowerCase() as
                  | 'explicit'
                  | 'clean'
                  | 'censored'
                  | 'unknown')
              : 'unknown',
            artworkPreSignedUrl: track.artwork_url || undefined,
            artworkThumbnailUrl: track.artwork_thumbnail_url ?? null,
            createDate: track.create_date,
          },
        ],
      };

      if (
        playerState.playingItem &&
        'collectionId' in playerState.playingItem &&
        playerState.playingItem.collectionId === track.id &&
        playerState.playIntent
      ) {
        playerState.togglePlayback();
      } else {
        playerState.clickCollection(
          singleTrackCollection,
          CollectionSortBy.CREATE_DATE,
        );
      }
    },
    [playerState, showToast],
  );

  const isTrackPlaying = useCallback(
    (trackId: number): boolean =>
      !!(
        playerState.playingItem &&
        'collectionId' in playerState.playingItem &&
        playerState.playingItem.collectionId === trackId &&
        playerState.playIntent
      ),
    [playerState],
  );

  // ─── Sorting ────────────────────────────────────────────────────────────────

  const handleSort = useCallback(
    (column: CatalogSortColumn) => {
      if (sortBy === column) {
        if (sortOrder === 'asc') {
          setSortOrder('desc');
        } else {
          setSortBy(null);
          setSortOrder('asc');
        }
      } else {
        setSortBy(column);
        setSortOrder('asc');
      }
      setPage(0);
    },
    [sortBy, sortOrder],
  );

  const setFilters = useCallback(
    (newFilters: CatalogFiltersType | undefined, resetPage = true) => {
      setFiltersState(newFilters);
      if (resetPage) {
        setPage(0);
      }
    },
    [],
  );

  const setSearchScope = useCallback((scope: SearchScope) => {
    setSearchScopeState(scope);
    setPage(0);
  }, []);

  return {
    tracks,
    loading,
    error,
    page,
    pageSize,
    total,
    searchQuery,
    searchScope,
    filters,
    sortBy,
    sortOrder,
    setPage,
    setPageSize,
    setSearchQuery,
    setSearchScope,
    setFilters,
    setSortBy,
    setSortOrder,
    handleSort,
    handlePlayTrack,
    isTrackPlaying,
  };
};
